/**
 * Atlas PM · 任务设计器 · 中列「任务指标与完成要素」
 */

import MetricCard from './MetricCard';
import type { ElementDraft, MetricDraft } from './types';

export interface MetricDesignerProps {
  metrics: MetricDraft[];
  onMetricChange: (key: string, patch: Partial<MetricDraft>) => void;
  onMetricRemove: (key: string) => void;
  onMetricDuplicate: (key: string) => void;
  onElementChange: (metricKey: string, elKey: string, patch: Partial<ElementDraft>) => void;
  onElementAdd: (metricKey: string) => void;
  onElementRemove: (metricKey: string, elKey: string) => void;
  onAddMetric: () => void;
}

export default function MetricDesigner({
  metrics,
  onMetricChange,
  onMetricRemove,
  onMetricDuplicate,
  onElementChange,
  onElementAdd,
  onElementRemove,
  onAddMetric,
}: MetricDesignerProps) {
  const elementCount = metrics.reduce((s, m) => s + m.elements.length, 0);
  const totalWeight = metrics.reduce((s, m) => s + (Number(m.weight) || 0), 0);
  const weightOk = Math.abs(totalWeight - 100) < 1e-9;

  return (
    <section className="td-col td-col--metric" aria-label="任务指标与完成要素">
      <header className="td-col__head">
        <h3 className="td-col__title">任务指标与完成要素</h3>
        <span className="td-col__extra">
          {metrics.length} 项指标 · {elementCount} 个要素
        </span>
      </header>

      <div className="td-col__body">
        {metrics.map((m) => (
          <MetricCard
            key={m.key}
            metric={m}
            onChange={(patch) => onMetricChange(m.key, patch)}
            onElementChange={(elKey, patch) => onElementChange(m.key, elKey, patch)}
            onElementRemove={(elKey) => onElementRemove(m.key, elKey)}
            onElementAdd={() => onElementAdd(m.key)}
            onRemove={() => onMetricRemove(m.key)}
            onDuplicate={() => onMetricDuplicate(m.key)}
          />
        ))}

        {metrics.length === 0 && (
          <div className="td-empty">还没有指标。点击下方「添加指标」，再逐个设置要素与权重。</div>
        )}

        <div className={['td-wsum', weightOk ? 'is-ok' : 'is-warn'].join(' ')} data-testid="metric-weight-sum">
          <span aria-hidden="true">{weightOk ? '✓' : '⚠'}</span>
          <span>
            指标权重合计 <b>{round(totalWeight)} / 100</b>
            {weightOk ? '（已满足）' : '（需合计等于 100 才能创建）'}
            <em className="td-wsum__hint">
              完成度 = Σ(要素占指标权重 × 要素完成度) / 100；质量同理，两条链路独立计算
            </em>
          </span>
        </div>

        <button type="button" className="td-add-metric" onClick={onAddMetric}>
          ＋ 添加指标（数量不限）
        </button>
      </div>
    </section>
  );
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
